/**
 * Утилита для парсинга медиа (фото и видео) из текста
 */

import { GalleryImage } from '@/components/ImageGallery';
import { GalleryVideo } from '@/components/VideoGallery';

/**
 * Результат парсинга медиа
 */
export interface ParsedMedia {
  /** Изображения из текста */
  images: GalleryImage[];
  /** Видео из текста */
  videos: GalleryVideo[];
  /** Текст без медиа тегов */
  cleanText: string;
}

/**
 * Сегмент контента — текст, изображение или видео в порядке появления
 */
export type ContentSegment =
  | { type: 'text'; content: string }
  | { type: 'image'; images: GalleryImage[] }
  | { type: 'video'; videos: GalleryVideo[] }
  | { type: 'review'; kitCode: string };

/**
 * Разбивает контент на сегменты, сохраняя порядок текста и медиа.
 *
 * Позволяет рендерить картинки/видео именно там, где они стоят в исходном
 * тексте нейронки, а не всегда в конце.
 *
 * Последовательные однотипные медиа-теги группируются в один сегмент
 * (отображаются как галерея).
 */
export function parseContentSegments(rawContent: string): ContentSegment[] {
  // Normalize literal \n and smart/curly quotes to standard equivalents
  const content = rawContent
    .replace(/\\n/g, '\n')
    .replace(/[\u201C\u201D\u201E\u201F\u00AB\u00BB]/g, '"');

  const mediaPattern = /\{\s*(img|video)\s*\}\s*(https?:\/\/[^\s{}]+)\s*\{\s*\1\s*\}[^\S\n]*([^\n{]*)/gi;
  const reviewPattern = /\[review\s+"([^"]+)"\]/gi;

  // Collect all matches with their positions
  interface MatchInfo { index: number; length: number; kind: 'img' | 'video' | 'review'; url?: string; caption?: string; kitCode?: string }
  const allMatches: MatchInfo[] = [];

  for (const m of content.matchAll(mediaPattern)) {
    allMatches.push({
      index: m.index!,
      length: m[0].length,
      kind: m[1].toLowerCase() as 'img' | 'video',
      url: m[2].trim(),
      caption: m[3].trim() || undefined,
    });
  }

  for (const m of content.matchAll(reviewPattern)) {
    allMatches.push({
      index: m.index!,
      length: m[0].length,
      kind: 'review',
      kitCode: m[1],
    });
  }

  allMatches.sort((a, b) => a.index - b.index);

  const segments: ContentSegment[] = [];
  let lastIndex = 0;

  for (const match of allMatches) {
    const textBefore = content.slice(lastIndex, match.index).trim().replace(/\n{3,}/g, '\n\n');
    if (textBefore) {
      segments.push({ type: 'text', content: textBefore });
    }

    if (match.kind === 'review') {
      segments.push({ type: 'review', kitCode: match.kitCode! });
    } else if (match.kind === 'img') {
      const prev = segments[segments.length - 1];
      if (prev?.type === 'image') {
        prev.images.push({ url: match.url!, caption: match.caption });
      } else {
        segments.push({ type: 'image', images: [{ url: match.url!, caption: match.caption }] });
      }
    } else {
      const prev = segments[segments.length - 1];
      if (prev?.type === 'video') {
        prev.videos.push({ url: match.url!, caption: match.caption });
      } else {
        segments.push({ type: 'video', videos: [{ url: match.url!, caption: match.caption }] });
      }
    }

    lastIndex = match.index + match.length;
  }

  const trailing = content.slice(lastIndex).trim().replace(/\n{3,}/g, '\n\n');
  if (trailing) {
    segments.push({ type: 'text', content: trailing });
  }

  return segments;
}

/**
 * Парсит текст и извлекает медиа контент (legacy, для обратной совместимости).
 * Для нового кода используйте parseContentSegments().
 */
export function parseMedia(content: string): ParsedMedia {
  const segments = parseContentSegments(content);
  const images: GalleryImage[] = [];
  const videos: GalleryVideo[] = [];
  const textParts: string[] = [];

  for (const seg of segments) {
    if (seg.type === 'text') textParts.push(seg.content);
    else if (seg.type === 'image') images.push(...seg.images);
    else if (seg.type === 'video') videos.push(...seg.videos);
  }

  return {
    images,
    videos,
    cleanText: textParts.join('\n\n'),
  };
}

/**
 * Проверяет, является ли URL YouTube ссылкой
 */
export function isYouTubeUrl(url: string): boolean {
  return /(?:youtube\.com|youtu\.be)/.test(url);
}

/**
 * Тип видео
 */
export type VideoType = 'youtube' | 'direct' | 'unknown';

/**
 * Определяет тип видео по URL
 * 
 * @param url - URL видео
 * @returns Тип видео: 'youtube' | 'direct' | 'unknown'
 * 
 * @example
 * getVideoType('https://youtube.com/watch?v=ABC123') // 'youtube'
 * getVideoType('https://example.com/video.mp4') // 'direct'
 * getVideoType('https://example.com/video') // 'unknown'
 */
export function getVideoType(url: string): VideoType {
  if (isYouTubeUrl(url)) {
    return 'youtube';
  }
  
  // Проверяем прямые ссылки на видео файлы
  // Поддерживаем основные форматы: mp4, webm, ogg, mov, avi
  const directVideoPattern = /\.(mp4|webm|ogg|mov|avi)(\?|$)/i;
  if (directVideoPattern.test(url)) {
    return 'direct';
  }
  
  return 'unknown';
}

/**
 * Обратная совместимость с parseImages
 * @deprecated Используйте parseMedia
 */
export function parseImages(content: string): { images: GalleryImage[]; cleanText: string } {
  const result = parseMedia(content);
  return {
    images: result.images,
    cleanText: result.cleanText,
  };
}

